/**
 * SizerSelectAutocomplete — a TYPE-OR-BROWSE select for a plain-state sizer form.
 *
 * The kit's `SelectInput` is a Menu-backed button: fine for three ladder columns
 * or four tiers, wrong for a list that grows with the business. The
 * correspondent picker is the case that broke it — every new counterparty makes
 * the menu longer, and there is no way to jump to one except scrolling past the
 * others.
 *
 * Same construction as `SizerAddressInput` (MUI Autocomplete normalized through
 * `SizerMuiField`, pinned to the kit's 42px control height) so it sits level
 * with the plain inputs beside it rather than reading as a borrowed control.
 *
 * `options` take the kit's usual shape: `[{value, label}]` or bare strings.
 * `value` is the VALUE, not the option object — the component resolves it — so
 * this drops into a form field wherever `SelectInput` sat, with the same
 * `onChange(value)` contract.
 */
import { useMemo, useState } from "react";

import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";

import Txt from "../../components/typography/Txt";
import { SZ, SizerMuiField } from "./sizerUi";

/**
 * `SizerMuiField` normalizes a portal MUI control onto the sizer look but sizes
 * to 38px; the kit's plain inputs are 42px. Pin it, for the same reason the RTL
 * sizer pins its address row — a 4px mismatch across a field row is visible.
 */
const FIELD_H = 42;
const FIELD_SX = {
  ".MuiOutlinedInput-root": {
    height: FIELD_H,
    minHeight: FIELD_H,
    py: 0,
    pr: "38px !important",
  },
  ".MuiAutocomplete-input": { py: "0 !important" },
};

export default function SizerSelectAutocomplete({
  value,
  onChange,
  options = [],
  placeholder,
  disabled,
  // Shown under the list when a typed query matches nothing.
  noOptionsText = "No matches",
}) {
  const [typed, setTyped] = useState(null);

  const norm = useMemo(
    () =>
      (options || []).map((o) =>
        typeof o === "object" ? o : { value: o, label: String(o) },
      ),
    [options],
  );

  const selected = useMemo(
    () => norm.find((o) => o.value === value) ?? null,
    [norm, value],
  );

  return (
    <SizerMuiField sx={FIELD_SX}>
      <Autocomplete
        disabled={disabled}
        options={norm}
        value={selected}
        /* While the user is typing, the RAW text wins; on blur the display goes
           back to whatever is actually selected. Without this the input snaps
           back to the selected label on every keystroke and cannot be searched. */
        inputValue={typed != null ? typed : selected?.label || ""}
        onInputChange={(e, next, reason) => {
          if (reason === "input") setTyped(next);
          if (reason === "reset" || reason === "clear") setTyped(null);
        }}
        onChange={(e, option) => {
          setTyped(null);
          onChange?.(option ? option.value : "");
        }}
        onBlur={() => setTyped(null)}
        getOptionLabel={(o) => o?.label ?? ""}
        isOptionEqualToValue={(o, v) => o.value === v?.value}
        noOptionsText={noOptionsText}
        renderOption={(props, option) => (
          <Box component="li" {...props} key={String(option.value)}>
            <Txt sx={{ fontSize: 13, color: SZ.ink900 }}>{option.label}</Txt>
          </Box>
        )}
        renderInput={(params) => (
          <TextField {...params} placeholder={placeholder} />
        )}
      />
    </SizerMuiField>
  );
}
